/* crm-workflow.jsx — Workflow foundation components for Phase 4
   Provides: WorkflowCard, TriggerEditor, ActionBuilder, WorkflowEditor,
             WorkflowHistory, WorkflowManagerView
   Self-registers on window. */

const { useState, useEffect, useMemo, useRef, useCallback } = React;

/* ── helpers ── */
const _fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const DEFAULT_TRIGGERS = [
  { type: 'DEAL_STAGE_CHANGED', label: 'Deal Stage Changed', icon: 'TrendingUp' },
  { type: 'DEAL_CREATED',       label: 'Deal Created',       icon: 'Target' },
  { type: 'DEAL_CLOSED_WON',    label: 'Deal Closed Won',    icon: 'CheckCircle' },
  { type: 'DEAL_CLOSED_LOST',   label: 'Deal Closed Lost',   icon: 'XCircle' },
  { type: 'CONTACT_CREATED',    label: 'Contact Created',    icon: 'User' },
  { type: 'TASK_COMPLETED',     label: 'Task Completed',     icon: 'CheckSquare' },
  { type: 'MEETING_SCHEDULED',  label: 'Meeting Scheduled',  icon: 'Calendar' },
  { type: 'MANUAL',             label: 'Manual Trigger',     icon: 'Play' },
];

const DEFAULT_ACTIONS = [
  { type: 'SEND_EMAIL',      label: 'Send Email' },
  { type: 'CREATE_TASK',     label: 'Create Task' },
  { type: 'UPDATE_FIELD',    label: 'Update Field' },
  { type: 'SEND_WEBHOOK',    label: 'Send Webhook' },
  { type: 'ADD_TAG',         label: 'Add Tag' },
  { type: 'REMOVE_TAG',      label: 'Remove Tag' },
  { type: 'ASSIGN_OWNER',    label: 'Assign Owner' },
  { type: 'SEND_NOTIFICATION', label: 'Send Notification' },
];

/* ══════════════════════════════════════════════════════════════
   STATUS PILL
   ══════════════════════════════════════════════════════════════ */
const StatusPill = ({ active }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
    borderRadius: 20, fontSize: 11, fontWeight: 600,
    background: active ? '#DCFCE7' : 'var(--nx-border)',
    color: active ? '#16A34A' : 'var(--nx-text-muted)',
  }}>
    <span style={{ width: 5, height: 5, borderRadius: '50%', background: active ? '#16A34A' : 'var(--nx-text-muted)' }} />
    {active ? 'Active' : 'Inactive'}
  </span>
);

/* ══════════════════════════════════════════════════════════════
   WORKFLOW CARD
   ══════════════════════════════════════════════════════════════ */
const WorkflowCard = ({ wf, onEdit, onDelete, onTrigger, onToggle }) => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const toast = useToast();
  const [toggling, setToggling] = useState(false);
  const triggerLabel = (DEFAULT_TRIGGERS.find(t => t.type === wf.triggerType) || {}).label || wf.triggerType || '—';
  const triggerIcon  = (DEFAULT_TRIGGERS.find(t => t.type === wf.triggerType) || {}).icon || 'Zap';

  const handleToggle = async () => {
    setToggling(true);
    try {
      await window.__nx_api?.workflow?.update(wsId, wf.id, { active: !wf.active });
      toast.show?.({ type: 'success', message: `Workflow ${!wf.active ? 'activated' : 'deactivated'}` });
      if (onToggle) onToggle();
    } catch {
      toast.show?.({ type: 'error', message: 'Failed to update workflow' });
    } finally {
      setToggling(false);
    }
  };

  return (
    <div style={{
      background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10,
      padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--nx-accent-tint-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <NexusIcon name="Zap" size={16} style={{ color: 'var(--nx-accent)' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--nx-text)' }}>{wf.name}</span>
            <StatusPill active={wf.active} />
          </div>
          {wf.description && <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{wf.description}</div>}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontSize: 12, color: 'var(--nx-text-muted)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <NexusIcon name={triggerIcon} size={12} /> {triggerLabel}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <NexusIcon name="List" size={12} /> {(wf.actions || []).length} action{(wf.actions || []).length !== 1 ? 's' : ''}
        </span>
        {wf.lastRunAt && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <NexusIcon name="Clock" size={12} /> Last run {_fmtDate(wf.lastRunAt)}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 4, borderTop: '1px solid var(--nx-border)' }}>
        <button onClick={handleToggle} disabled={toggling} style={{ fontSize: 12, color: wf.active ? '#F59E0B' : '#10B981', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontWeight: 600 }}>
          {wf.active ? 'Deactivate' : 'Activate'}
        </button>
        {wf.triggerType === 'MANUAL' && (
          <button onClick={() => onTrigger?.(wf)} style={{ fontSize: 12, color: 'var(--nx-accent)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
            <NexusIcon name="Play" size={11} /> Run Now
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => onEdit?.(wf)} style={{ fontSize: 12, color: 'var(--nx-text-muted)', background: 'transparent', border: '1px solid var(--nx-border)', cursor: 'pointer', padding: '4px 10px', borderRadius: 6 }}>Edit</button>
        <button onClick={() => onDelete?.(wf)} style={{ fontSize: 12, color: '#EF4444', background: 'transparent', border: '1px solid #EF444430', cursor: 'pointer', padding: '4px 10px', borderRadius: 6 }}>Delete</button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   TRIGGER EDITOR
   ══════════════════════════════════════════════════════════════ */
const TriggerEditor = ({ value, onChange }) => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const [triggers, setTriggers] = useState(DEFAULT_TRIGGERS);
  const [config, setConfig] = useState(value?.config || {});

  useEffect(() => {
    (async () => {
      try {
        const d = await window.__nx_api?.workflow?.catalog?.(wsId);
        if (d && Array.isArray(d?.triggers)) setTriggers(d.triggers);
      } catch(e) {}
    })();
  }, [wsId]);

  const selectedType = value?.type || '';

  const handleTypeChange = (type) => {
    setConfig({});
    onChange?.({ type, config: {} });
  };

  const handleConfigChange = (k, v) => {
    const next = { ...config, [k]: v };
    setConfig(next);
    onChange?.({ type: selectedType, config: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <select
        value={selectedType}
        onChange={e => handleTypeChange(e.target.value)}
        style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--nx-border)', fontSize: 13, background: 'var(--nx-surface)', color: 'var(--nx-text)', width: '100%' }}
      >
        <option value="">— Select trigger —</option>
        {triggers.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
      </select>
      {selectedType && (
        <div style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Trigger Configuration</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input placeholder="Filter / condition (optional)" value={config.filter || ''} onChange={e => handleConfigChange('filter', e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--nx-border)', fontSize: 12, background: 'var(--nx-surface)', color: 'var(--nx-text)' }} />
            <textarea placeholder="Additional config (JSON, optional)" value={config.extra || ''} onChange={e => handleConfigChange('extra', e.target.value)}
              rows={2} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--nx-border)', fontSize: 12, background: 'var(--nx-surface)', color: 'var(--nx-text)', resize: 'vertical', fontFamily: 'monospace' }} />
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   ACTION BUILDER
   ══════════════════════════════════════════════════════════════ */
const ActionBuilder = ({ value, onChange }) => {
  const [actions, setActions] = useState(Array.isArray(value) ? value : []);

  const notify = (updated) => {
    setActions(updated);
    onChange?.(updated);
  };

  const addAction = () => notify([...actions, { type: '', label: '', id: Date.now() }]);

  const updateAction = (idx, field, val) => {
    const next = actions.map((a, i) => i === idx ? { ...a, [field]: val } : a);
    notify(next);
  };

  const removeAction = (idx) => notify(actions.filter((_, i) => i !== idx));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {actions.map((a, idx) => (
        <div key={a.id || idx} style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '8px 10px' }}>
          <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', minWidth: 22, textAlign: 'center', fontWeight: 600 }}>{idx + 1}</span>
          <select value={a.type} onChange={e => updateAction(idx, 'type', e.target.value)}
            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--nx-border)', fontSize: 12, background: 'var(--nx-surface)', color: 'var(--nx-text)', flex: 1 }}>
            <option value="">— Action type —</option>
            {DEFAULT_ACTIONS.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
          </select>
          <input placeholder="Config / label" value={a.label || ''} onChange={e => updateAction(idx, 'label', e.target.value)}
            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--nx-border)', fontSize: 12, background: 'var(--nx-surface)', color: 'var(--nx-text)', flex: 2 }} />
          <button onClick={() => removeAction(idx)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#EF4444', display: 'flex', alignItems: 'center' }}>
            <NexusIcon name="Trash2" size={14} />
          </button>
        </div>
      ))}
      <button onClick={addAction} style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8,
        border: '1px dashed var(--nx-border)', background: 'transparent', cursor: 'pointer',
        fontSize: 13, color: 'var(--nx-accent)', fontWeight: 600,
      }}>
        <NexusIcon name="Plus" size={14} /> Add Action
      </button>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   WORKFLOW EDITOR
   ══════════════════════════════════════════════════════════════ */
const WorkflowEditor = ({ workflow, onSave, onCancel }) => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: workflow?.name || '',
    description: workflow?.description || '',
    trigger: { type: workflow?.triggerType || '', config: workflow?.triggerConfig || {} },
    conditions: workflow?.conditions ? JSON.stringify(workflow.conditions, null, 2) : '',
    actions: workflow?.actions || [],
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.show?.({ type: 'error', message: 'Name is required' }); return; }
    if (!form.trigger.type) { toast.show?.({ type: 'error', message: 'Select a trigger type' }); return; }
    setSaving(true);
    try {
      let parsedConds = undefined;
      if (form.conditions.trim()) {
        try { parsedConds = JSON.parse(form.conditions); }
        catch { toast.show?.({ type: 'error', message: 'Conditions must be valid JSON' }); setSaving(false); return; }
      }
      const payload = {
        name: form.name.trim(), description: form.description.trim(),
        triggerType: form.trigger.type, triggerConfig: form.trigger.config,
        conditions: parsedConds, actions: form.actions,
      };
      let result;
      if (workflow?.id) {
        result = await window.__nx_api?.workflow?.update(wsId, workflow.id, payload);
      } else {
        result = await window.__nx_api?.workflow?.create(wsId, payload);
      }
      toast.show?.({ type: 'success', message: `Workflow ${workflow?.id ? 'updated' : 'created'}` });
      onSave?.(result);
    } catch (err) {
      toast.show?.({ type: 'error', message: err?.message || 'Failed to save workflow' });
    } finally {
      setSaving(false);
    }
  };

  const label = (txt, required) => (
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 6 }}>
      {txt}{required && <span style={{ color: '#EF4444' }}> *</span>}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: 20 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--nx-text)' }}>{workflow?.id ? 'Edit Workflow' : 'New Workflow'}</div>
      <div>
        {label('Name', true)}
        <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Workflow name"
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--nx-border)', fontSize: 13, background: 'var(--nx-surface)', color: 'var(--nx-text)', boxSizing: 'border-box' }} />
      </div>
      <div>
        {label('Description')}
        <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional description" rows={2}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--nx-border)', fontSize: 13, background: 'var(--nx-surface)', color: 'var(--nx-text)', resize: 'vertical', boxSizing: 'border-box' }} />
      </div>
      <div>
        {label('Trigger', true)}
        <TriggerEditor value={form.trigger} onChange={v => set('trigger', v)} />
      </div>
      <div>
        {label('Conditions (JSON, optional)')}
        <textarea value={form.conditions} onChange={e => set('conditions', e.target.value)} placeholder='e.g. {"field":"stage","op":"eq","value":"Proposal"}' rows={3}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--nx-border)', fontSize: 12, fontFamily: 'monospace', background: 'var(--nx-surface)', color: 'var(--nx-text)', resize: 'vertical', boxSizing: 'border-box' }} />
      </div>
      <div>
        {label('Actions')}
        <ActionBuilder value={form.actions} onChange={v => set('actions', v)} />
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--nx-border)' }}>
        <button onClick={onCancel} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--nx-border)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--nx-text)' }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--nx-accent)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save Workflow'}
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   WORKFLOW HISTORY
   ══════════════════════════════════════════════════════════════ */
const RUN_STATUS_PILL = { COMPLETED: { bg: '#DCFCE7', color: '#16A34A' }, FAILED: { bg: '#FEE2E2', color: '#DC2626' }, RUNNING: { bg: '#EFF6FF', color: '#2563EB' } };

const WorkflowHistory = ({ workflowId }) => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await window.__nx_api?.workflow?.history?.(wsId, workflowId);
        if (d) setRuns(Array.isArray(d) ? d : (d?.items || []));
      } catch(e) { setRuns([]); }
      setLoading(false);
    })();
  }, [wsId, workflowId]);

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading history…</div>;
  if (!runs.length) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <NexusIcon name="Clock" size={24} style={{ color: 'var(--nx-text-muted)', marginBottom: 10 }} />
      <div style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>No run history yet</div>
    </div>
  );

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--nx-surface-alt)' }}>
            {['Workflow', 'Status', 'Trigger', 'Ran At'].map(h => (
              <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--nx-border)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {runs.map((r, i) => {
            const sp = RUN_STATUS_PILL[r.status] || { bg: 'var(--nx-border)', color: 'var(--nx-text-muted)' };
            return (
              <tr key={r.id || i} style={{ borderBottom: '1px solid var(--nx-border)' }}>
                <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--nx-text)' }}>{r.workflowName || r.name || '—'}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: sp.bg, color: sp.color }}>{r.status}</span>
                </td>
                <td style={{ padding: '10px 14px', color: 'var(--nx-text-muted)', fontSize: 12 }}>{r.triggerSummary || r.trigger || '—'}</td>
                <td style={{ padding: '10px 14px', color: 'var(--nx-text-muted)', fontSize: 12 }}>{_fmtDate(r.ranAt || r.createdAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   WORKFLOW MANAGER VIEW
   ══════════════════════════════════════════════════════════════ */
const WF_TABS = ['Workflows', 'Event Catalog', 'History'];

const WorkflowManagerView = () => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const store = useStore?.();
  const toast = useToast();
  const [tab, setTab] = useState('Workflows');
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [catalog, setCatalog] = useState({ triggers: DEFAULT_TRIGGERS, actions: DEFAULT_ACTIONS });

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await window.__nx_api?.workflow?.list(wsId);
      setWorkflows(Array.isArray(data) ? data : (data?.items || []));
    } catch { setWorkflows([]); } finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => {
    loadWorkflows();
    (async () => {
      try {
        const d = await window.__nx_api?.workflow?.catalog?.(wsId);
        if (d) setCatalog({ triggers: d.triggers || DEFAULT_TRIGGERS, actions: d.actions || DEFAULT_ACTIONS });
      } catch(e) {}
    })();
  }, [loadWorkflows]);

  const handleDelete = (wf) => {
    store?.confirm({
      title: `Delete workflow "${wf.name}"?`,
      description: 'This action cannot be undone.',
      danger: true,
      onConfirm: async () => {
        try {
          await window.__nx_api?.workflow?.delete(wsId, wf.id);
          toast.show?.({ type: 'success', message: 'Workflow deleted' });
          loadWorkflows();
        } catch { toast.show?.({ type: 'error', message: 'Failed to delete' }); }
      },
    });
  };

  const handleTrigger = async (wf) => {
    try {
      await window.__nx_api?.workflow?.trigger(wsId, wf.id, {});
      toast.show?.({ type: 'success', message: `Workflow "${wf.name}" triggered` });
    } catch { toast.show?.({ type: 'error', message: 'Failed to trigger workflow' }); }
  };

  const handleSave = () => { setShowEditor(false); setEditing(null); loadWorkflows(); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--nx-bg)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 24px', borderBottom: '1px solid var(--nx-border)', background: 'var(--nx-surface)' }}>
        <NexusIcon name="Zap" size={18} style={{ color: 'var(--nx-accent)' }} />
        <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--nx-text)' }}>Workflows</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => { setEditing(null); setShowEditor(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--nx-accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          <NexusIcon name="Plus" size={14} /> New Workflow
        </button>
      </div>
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--nx-border)', padding: '0 24px', background: 'var(--nx-surface)' }}>
        {WF_TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 14px', fontSize: 13, fontWeight: tab === t ? 700 : 500, color: tab === t ? 'var(--nx-accent)' : 'var(--nx-text-muted)', background: 'transparent', border: 'none', cursor: 'pointer', borderBottom: tab === t ? '2px solid var(--nx-accent)' : '2px solid transparent', marginBottom: -1 }}>{t}</button>
        ))}
      </div>
      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        {tab === 'Workflows' && (
          loading ? <div style={{ textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13, paddingTop: 40 }}>Loading…</div>
          : workflows.length === 0 ? (
            <div style={{ textAlign: 'center', paddingTop: 60 }}>
              <NexusIcon name="Zap" size={32} style={{ color: 'var(--nx-text-muted)', marginBottom: 12 }} />
              <div style={{ fontSize: 14, color: 'var(--nx-text-muted)', marginBottom: 16 }}>No workflows yet</div>
              <button onClick={() => { setEditing(null); setShowEditor(true); }} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--nx-accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Create First Workflow</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {workflows.map(wf => (
                <WorkflowCard key={wf.id} wf={wf} onEdit={w => { setEditing(w); setShowEditor(true); }} onDelete={handleDelete} onTrigger={handleTrigger} onToggle={loadWorkflows} />
              ))}
            </div>
          )
        )}
        {tab === 'Event Catalog' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 12 }}>Trigger Types</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {catalog.triggers.map(t => (
                  <div key={t.type} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8 }}>
                    <NexusIcon name={t.icon || 'Zap'} size={14} style={{ color: 'var(--nx-accent)' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 12 }}>Action Types</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {catalog.actions.map(a => (
                  <div key={a.type} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8 }}>
                    <NexusIcon name="Play" size={14} style={{ color: '#10B981' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>{a.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {tab === 'History' && <WorkflowHistory />}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--nx-surface)', borderRadius: 12, width: 600, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <WorkflowEditor workflow={editing} onSave={handleSave} onCancel={() => { setShowEditor(false); setEditing(null); }} />
          </div>
        </div>
      )}
    </div>
  );
};

/* ── registrations ── */
window.WorkflowCard = WorkflowCard;
window.TriggerEditor = TriggerEditor;
window.ActionBuilder = ActionBuilder;
window.WorkflowEditor = WorkflowEditor;
window.WorkflowHistory = WorkflowHistory;
window.WorkflowManagerView = WorkflowManagerView;
